import { useState, useMemo } from 'react';
import { format, subDays, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, subMonths } from 'date-fns';
import { vi } from 'date-fns/locale';
import { ChevronDown, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { toVnDateString } from "@apv-erp/status";
export type TimelineOption = 'all' | 'last30days' | 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'lastQuarter';

export interface TimelineRange {
  startDate: Date | null;
  endDate: Date | null;
  label: string;
}

export function getTimelineRange(option: TimelineOption): TimelineRange {
  const today = new Date();

  switch (option) {
    case 'last30days':
      return {
        startDate: subDays(today, 30),
        endDate: today,
        label: 'Last 30 days'
      };
    case 'thisMonth':
      return {
        startDate: startOfMonth(today),
        endDate: endOfMonth(today),
        label: 'This month'
      };
    case 'lastMonth':
      const lastMonth = subMonths(today, 1);
      return {
        startDate: startOfMonth(lastMonth),
        endDate: endOfMonth(lastMonth),
        label: 'Last month'
      };
    case 'thisQuarter':
      return {
        startDate: startOfQuarter(today),
        endDate: endOfQuarter(today),
        label: 'This quarter'
      };
    case 'lastQuarter':
      const lastQ = subMonths(today, 3);
      return {
        startDate: startOfQuarter(lastQ),
        endDate: endOfQuarter(lastQ),
        label: 'Last quarter'
      };
    case 'all':
    default:
      return {
        startDate: null,
        endDate: null,
        label: 'All'
      };
  }
}

export function TimelineFilter({
  value = 'last30days',
  onChange,
}: {
  value?: TimelineOption;
  onChange: (option: TimelineOption) => void;
}) {
  const range = useMemo(() => getTimelineRange(value), [value]);
  const dateRangeText = useMemo(() => {
    if (!range.startDate || !range.endDate) return 'All time';
    return `${format(range.startDate, 'dd/MM')} - ${format(range.endDate, 'dd/MM/yyyy')}`;
  }, [range]);

  const labelMap: Record<TimelineOption, string> = {
    all: 'Tất cả',
    last30days: '30 ngày gần nhất',
    thisMonth: 'Tháng này',
    lastMonth: 'Tháng trước',
    thisQuarter: 'Quý này',
    lastQuarter: 'Quý trước',
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 h-8 text-xs">
          <Calendar className="h-3.5 w-3.5" />
          <span>{labelMap[value]}</span>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel className="text-xs font-semibold text-slate-600">Lọc theo thời gian</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={() => onChange('last30days')} className={value === 'last30days' ? 'bg-indigo-50' : ''}>
          <div className="flex items-center justify-between w-full">
            <span className="text-xs">30 ngày gần nhất</span>
            {value === 'last30days' && <span className="text-[10px] text-indigo-600 font-semibold">✓</span>}
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={() => onChange('thisMonth')} className={value === 'thisMonth' ? 'bg-indigo-50' : ''}>
          <div className="flex items-center justify-between w-full">
            <span className="text-xs">Tháng này</span>
            {value === 'thisMonth' && <span className="text-[10px] text-indigo-600 font-semibold">✓</span>}
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={() => onChange('lastMonth')} className={value === 'lastMonth' ? 'bg-indigo-50' : ''}>
          <div className="flex items-center justify-between w-full">
            <span className="text-xs">Tháng trước</span>
            {value === 'lastMonth' && <span className="text-[10px] text-indigo-600 font-semibold">✓</span>}
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={() => onChange('thisQuarter')} className={value === 'thisQuarter' ? 'bg-indigo-50' : ''}>
          <div className="flex items-center justify-between w-full">
            <span className="text-xs">Quý này</span>
            {value === 'thisQuarter' && <span className="text-[10px] text-indigo-600 font-semibold">✓</span>}
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={() => onChange('lastQuarter')} className={value === 'lastQuarter' ? 'bg-indigo-50' : ''}>
          <div className="flex items-center justify-between w-full">
            <span className="text-xs">Quý trước</span>
            {value === 'lastQuarter' && <span className="text-[10px] text-indigo-600 font-semibold">✓</span>}
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={() => onChange('all')} className={value === 'all' ? 'bg-indigo-50' : ''}>
          <div className="flex items-center justify-between w-full">
            <span className="text-xs">Tất cả</span>
            {value === 'all' && <span className="text-[10px] text-indigo-600 font-semibold">✓</span>}
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function filterByTimeline(items: any[], option: TimelineOption, dateField = 'createdAt'): any[] {
  const range = getTimelineRange(option);

  if (!range.startDate || !range.endDate) return items;

  return items.filter(item => {
    const itemDate = new Date(item[dateField]);
    return itemDate >= range.startDate! && itemDate <= range.endDate!;
  });
}

export function useTimelineFilter(defaultOption: TimelineOption = 'all') {
  const [timelineOption, setTimelineOption] = useState<TimelineOption>(defaultOption);
  
  const dateParams = useMemo(() => {
    const range = getTimelineRange(timelineOption);
    if (!range.startDate || !range.endDate) return {};
    
    return {
      from: toVnDateString(range.startDate),
      to: toVnDateString(range.endDate)
    };
  }, [timelineOption]);

  return { timelineOption, setTimelineOption, dateParams };
}
